import { motion } from 'framer-motion'
import { useLanguage } from '../contexts/LanguageContext.jsx'
import { Dumbbell, Instagram, Twitter, Facebook, ArrowUp } from 'lucide-react'
import { useState, useEffect } from 'react'
import { siteSettingsService } from '../services/database.js'

function Footer() {
  const { language, t } = useLanguage()
  const [siteSettings, setSiteSettings] = useState({
    gymName: 'OXYGEN',
    address: language === 'ar' ? 'شارع الملك فهد، الرياض' : 'King Fahd Road, Riyadh',
    phone: '+966 50 123 4567',
    email: 'info@oxygengym.com',
    socialMedia: {
      instagram: '',
      twitter: '',
      facebook: '',
      snapchat: ''
    }
  })

  useEffect(() => {
    const loadSettings = async () => {
      try {
        const settingsData = await siteSettingsService.getAll()
        if (settingsData && settingsData.length > 0) {
          const settings = settingsData[0]
          setSiteSettings({
            gymName: settings.gym_name || 'OXYGEN',
            address: settings.address || siteSettings.address,
            phone: settings.phone || siteSettings.phone,
            email: settings.email || siteSettings.email,
            socialMedia: settings.social_media || siteSettings.socialMedia
          })
        }
      } catch (error) {
        console.error('Error loading footer settings:', error)
      }
    }
    loadSettings()
  }, [])

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  // Helper function to ensure URLs have https://
  const ensureHttps = (url) => {
    if (!url) return null
    if (url.startsWith('http://') || url.startsWith('https://')) {
      return url
    }
    return `https://${url}`
  }

  const socialLinks = {
    instagram: ensureHttps(siteSettings.socialMedia?.instagram),
    twitter: ensureHttps(siteSettings.socialMedia?.twitter),
    facebook: ensureHttps(siteSettings.socialMedia?.facebook),
    snapchat: ensureHttps(siteSettings.socialMedia?.snapchat)
  }

  return (
    <footer className="bg-darker border-t border-white/5 py-12">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid md:grid-cols-4 gap-8 mb-12">
          <div className="md:col-span-2">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-gradient-to-br from-yellow-400 to-orange-500 rounded-lg flex items-center justify-center">
                <Dumbbell className="w-6 h-6 text-black" />
              </div>
              <span className="text-2xl font-bold text-gradient">{siteSettings.gymName}</span>
            </div>
            <p className="text-gray-400 mb-6 max-w-sm">
              {language === 'ar' 
                ? `نادي ${siteSettings.gymName} - وجهتك الأولى للياقة البدنية والقوة. نقدم لك تجربة رياضية فريدة مع أحدث المعدات والمدربين المحترفين.`
                : `${siteSettings.gymName} - Your first destination for fitness and strength. We offer you a unique sports experience with the latest equipment and professional trainers.`}
            </p>
            <div className="flex gap-4">
              {socialLinks.instagram && (
                <a 
                  href={socialLinks.instagram} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-yellow-400 hover:text-black transition-all duration-300"
                >
                  <Instagram className="w-5 h-5" />
                </a>
              )}
              {socialLinks.twitter && (
                <a 
                  href={socialLinks.twitter} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-yellow-400 hover:text-black transition-all duration-300"
                >
                  <Twitter className="w-5 h-5" />
                </a>
              )}
              {socialLinks.facebook && (
                <a 
                  href={socialLinks.facebook} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="w-10 h-10 rounded-full bg-white/5 flex items-center justify-center text-gray-400 hover:bg-yellow-400 hover:text-black transition-all duration-300"
                >
                  <Facebook className="w-5 h-5" />
                </a>
              )}
            </div>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">{language === 'ar' ? 'روابط سريعة' : 'Quick Links'}</h4>
            <ul className="space-y-2">
              <li><a href="#home" className="text-gray-400 hover:text-yellow-400 transition-colors">{t('home')}</a></li>
              <li><a href="#features" className="text-gray-400 hover:text-yellow-400 transition-colors">{language === 'ar' ? 'مميزاتنا' : 'Features'}</a></li>
              <li><a href="#offers" className="text-gray-400 hover:text-yellow-400 transition-colors">{t('offers')}</a></li>
              <li><a href="#gallery" className="text-gray-400 hover:text-yellow-400 transition-colors">{t('gallery')}</a></li>
            </ul>
          </div>

          <div>
            <h4 className="text-white font-bold mb-4">{t('getInTouch')}</h4>
            <ul className="space-y-2 text-gray-400">
              <li>{siteSettings.address}</li>
              <li dir="ltr">{siteSettings.phone}</li>
              <li>{siteSettings.email}</li>
            </ul>
          </div>
        </div>

        <div className="border-t border-white/5 pt-8 flex flex-col md:flex-row items-center justify-between gap-4">
          <p className="text-gray-500 text-sm">
            © 2024 {siteSettings.gymName}. {t('allRightsReserved')}.
          </p>
          <button
            onClick={scrollToTop}
            className="w-10 h-10 rounded-full bg-yellow-400/20 flex items-center justify-center text-yellow-400 hover:bg-yellow-400 hover:text-black transition-all duration-300"
          >
            <ArrowUp className="w-5 h-5" />
          </button>
        </div>
      </div>
    </footer>
  )
}

export default Footer
